import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button, Typography } from "@mui/material";
import { getLastRecordManagementError } from "app/class/record-management";
import { fetchGatePublicTranslations, GatePublicTranslation } from "modules/gate/services/gatePublicTranslations";
import { buildGateRuntimeHtml } from "modules/gate/services/gateRuntimeHtml";
import { invokeGateHostMethodWithContext, isGateMethodAllowed } from "modules/gate/services/gateBridgeHost";
import { NavigateFunction } from "react-router-dom";
import {
    GateApiScope,
    GateDefinition,
    GateBridgeErrorPayload,
    GateBridgeLocationSyncMessage,
    GateBridgeRequestMessage,
    GateBridgeResponseMessage,
    GateLocationSnapshot,
    GateRuntimeContextSyncMessage,
    GateRuntimeCurrencyPayload,
} from "modules/gate/types/gate";
import i18n from "app/config/i18n";
import { useTranslation } from "react-i18next";
import useNotification from "component/global/event-handler/hooks/use-notification";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import { platformThemeCssVariables } from "app/theme/platformTheme";
import { useSession } from "app/hooks/useSession";
import { useTranslationResolver } from "app/hooks/useTranslationResolver";
import { useCurrencyPreferences } from "modules/record/shared/hooks/useCurrencyPreferences";
import { resolveGateRuntimeFeatures } from "modules/gate/services/gateRuntimeFeatures";
import { GATE_VUE_RUNTIME_QUERY_KEY, fetchGateVueRuntimeSource } from "modules/gate/services/gateVendorAssets";
import { useRenderLoopGuard } from "modules/gate/hooks/useRenderLoopGuard";

export interface PortalFrameProps {
    page: GateDefinition;
    hostLocation: GateLocationSnapshot;
    navigate: NavigateFunction;
}

export const GATE_READY_TIMEOUT_MS = 15_000;

type GateDiagnosticReason = "timeout" | "runtime-error" | "render-loop";
type GateDiagnostic = {
    ts: string;
    slug: string;
    reason: GateDiagnosticReason;
    iframeLoadCount: number;
    srcDocLength: number;
    bridgeMessages: Record<string, number>;
    runtimeSourceStatus: string;
    error: string | null;
    phase: string | null;
    changed?: string[];
    renders?: number;
};

declare global {
    interface Window { __PRM_GATE_DIAG?: GateDiagnostic[]; }
}

const mergeMessages = (base: Record<string, unknown>, extension: Record<string, unknown>): Record<string, unknown> => {
    return Object.entries(extension || {}).reduce<Record<string, unknown>>((accumulator, [key, value]) => {
        const currentValue = accumulator[key];

        if (
            value
            && typeof value === "object"
            && !Array.isArray(value)
            && currentValue
            && typeof currentValue === "object"
            && !Array.isArray(currentValue)
        ) {
            accumulator[key] = mergeMessages(
                currentValue as Record<string, unknown>,
                value as Record<string, unknown>
            );
            return accumulator;
        }

        accumulator[key] = value;
        return accumulator;
    }, { ...base });
};

const normalizeGateLocale = (value?: unknown) => {
    const rawValue = Array.isArray(value) ? value[0] : value;
    const normalizedValue = String(rawValue || "").trim();
    const lowerValue = normalizedValue.toLowerCase();

    if (lowerValue.startsWith("pt")) {
        return "pt-BR";
    }

    if (lowerValue.startsWith("en")) {
        return "en-US";
    }

    return undefined;
};

const assignMessagePath = (target: Record<string, unknown>, path: string, value: string) => {
    const parts = path.split(".").map((part) => part.trim()).filter(Boolean);
    if (!parts.length) {
        return;
    }

    let cursor = target;

    parts.slice(0, -1).forEach((part) => {
        const current = cursor[part];

        if (!current || typeof current !== "object" || Array.isArray(current)) {
            cursor[part] = {};
        }

        cursor = cursor[part] as Record<string, unknown>;
    });

    cursor[parts[parts.length - 1]] = value;
};

const translationsToMessages = (records: GatePublicTranslation[], language: string) => {
    return records
        .filter((record) => record.language === language)
        .reduce<Record<string, unknown>>((messages, record) => {
            if (record.text_key && record.text_value !== undefined) {
                assignMessagePath(messages, record.text_key, record.text_value);
            }

            return messages;
        }, {});
};

const performanceMark = (name: string) => {
    if (typeof performance !== "undefined" && performance.mark) {
        performance.mark(name);
    }
};

const performanceMeasure = (name: string, startMark: string) => {
    if (typeof performance === "undefined" || !performance.measure) return;

    try {
        performance.measure(name, startMark);
    } catch (_error) {
        // Performance marks are diagnostic only.
    }
};

const logGateLoadTimings = (
    pageMetric: string,
    runtimeTiming?: {
        vueScriptMs?: number | null;
        mountMs?: number;
        vueResourceTiming?: { transferSize?: number; queueMs?: number; ttfbMs?: number; downloadMs?: number; totalFetchMs?: number } | null;
    }
) => {
    if (typeof performance === "undefined" || !performance.getEntriesByType) return;
    if (typeof window === "undefined" || !window.location?.search?.includes("primumGateTrace")) return;

    const relevantPrefixes = ["primum.gate.bootstrap.", "primum.gate.translations.", "primum.gate.build-html.", "primum.gate.iframe", "primum.gate.ready"];
    const entries = performance.getEntriesByType("measure")
        .filter((entry) => relevantPrefixes.some((prefix) => entry.name.startsWith(prefix)))
        .map((entry) => ({ step: entry.name, durationMs: Math.round(entry.duration) }));

    if (typeof runtimeTiming?.vueScriptMs === "number") {
        entries.push({ step: "primum.gate.runtime.vue-script (inside iframe)", durationMs: runtimeTiming.vueScriptMs });
    }

    if (typeof runtimeTiming?.mountMs === "number") {
        entries.push({ step: "primum.gate.runtime.mount (inside iframe)", durationMs: runtimeTiming.mountMs });
    }

    const resourceTiming = runtimeTiming?.vueResourceTiming;
    if (resourceTiming) {
        if (typeof resourceTiming.queueMs === "number") {
            entries.push({ step: "primum.gate.runtime.vue-script.queue (fetchStart→requestStart)", durationMs: resourceTiming.queueMs });
        }
        if (typeof resourceTiming.ttfbMs === "number") {
            entries.push({ step: "primum.gate.runtime.vue-script.ttfb (requestStart→responseStart)", durationMs: resourceTiming.ttfbMs });
        }
        if (typeof resourceTiming.downloadMs === "number") {
            entries.push({ step: "primum.gate.runtime.vue-script.download (responseStart→responseEnd)", durationMs: resourceTiming.downloadMs });
        }
        if (typeof resourceTiming.totalFetchMs === "number") {
            entries.push({ step: "primum.gate.runtime.vue-script.total-fetch (fetchStart→responseEnd)", durationMs: resourceTiming.totalFetchMs });
        }
        if (typeof resourceTiming.transferSize === "number") {
            entries.push({ step: "primum.gate.runtime.vue-script.transferSize (bytes, 0 = served from cache)", durationMs: resourceTiming.transferSize });
        }
    }

    if (!entries.length) return;

    // eslint-disable-next-line no-console
    console.groupCollapsed(`[Gate] load timings — ${pageMetric}`);
    // eslint-disable-next-line no-console
    console.table(entries);
    // eslint-disable-next-line no-console
    console.groupEnd();
};

const DEFAULT_GATE_TRANSLATION_LANGUAGES = ["en-US", "pt-BR"];
const FORM_VALIDATION_TRANSLATION_KEYS = [
    "forms.validation.required",
    "forms.validation.email",
    "forms.validation.url",
    "forms.validation.minlength",
    "forms.validation.maxlength",
    "forms.validation.password_pattern",
    "forms.validation.password_pattern_invalid",
    "forms.validation.custom",
];
const GATE_LOCALE_STORAGE_KEY = "prm.gate.locale";

const readStoredGuestLocale = () => {
    try {
        return normalizeGateLocale(window.localStorage?.getItem(GATE_LOCALE_STORAGE_KEY));
    } catch (_error) {
        return undefined;
    }
};

const writeStoredGuestLocale = (locale: string) => {
    try {
        window.localStorage?.setItem(GATE_LOCALE_STORAGE_KEY, locale);
    } catch (_error) {
        // Storage can be unavailable in private/sandboxed contexts; runtime state still updates.
    }
};

const readGateInlineMessages = (page: GateDefinition, locale: string) => {
    const i18nMeta = page.meta?.i18n;
    if (!i18nMeta || typeof i18nMeta !== "object") return {};
    const messages = (i18nMeta as Record<string, unknown>)[locale];
    return messages && typeof messages === "object" && !Array.isArray(messages)
        ? messages as Record<string, unknown>
        : {};
};

const readGateSupportedLocales = (page: GateDefinition) => {
    const i18nMeta = page.meta?.i18n;
    if (!i18nMeta || typeof i18nMeta !== "object") return [];
    const metadata = i18nMeta as Record<string, unknown>;
    const configured = Array.isArray(metadata.supportedLocales) ? metadata.supportedLocales : [];
    const inlineLocales = Object.keys(metadata).filter((key) => normalizeGateLocale(key));
    return [...configured, ...inlineLocales]
        .map((locale) => normalizeGateLocale(locale))
        .filter(Boolean) as string[];
};

const toBridgePayload = (value: unknown) => {
    if (value === undefined) {
        return undefined;
    }

    return JSON.parse(JSON.stringify(value));
};

const resolveGateBasePath = (location: GateLocationSnapshot) => {
    const routePath = location.routePath || "/";

    if (routePath !== "/" && location.pathname.endsWith(routePath)) {
        return location.pathname.slice(0, -routePath.length) || "/";
    }

    return location.pathname.replace(/\/$/, "") || "/";
};

// Surfaces the backend's { code, message, details } envelope (axios error.response.data.error)
// to the widget/form running inside the iframe, instead of collapsing every failure down to
// axios' generic "Request failed with status code 422" -- callers like a form's onError handler
// need the real reason to show the user or log it.
export const extractGateInvokeError = (error: unknown): GateBridgeErrorPayload => {
    const response = (error as { response?: { status?: number; data?: { error?: string | { code?: string; message?: string; details?: unknown } } } })?.response;
    const apiError = response?.data?.error;

    if (typeof apiError === "string") {
        return { message: apiError, status: response?.status };
    }

    if (apiError?.message) {
        return { message: apiError.message, code: apiError.code, details: apiError.details, status: response?.status };
    }

    return { message: error instanceof Error ? error.message : "Unknown Gate error", status: response?.status };
};

const postGateMessage = (target: Window | null | undefined, message: GateBridgeResponseMessage | GateBridgeLocationSyncMessage | GateRuntimeContextSyncMessage) => {
    if (!target) {
        return;
    }

    target.postMessage(toBridgePayload(message), "*");
};

const PortalFrameComponent = ({ page, hostLocation, navigate }: PortalFrameProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const initialHostLocationRef = useRef(hostLocation);
    const [runtimeReady, setRuntimeReady] = useState(false);
    const [runtimeReadyGeneration, setRuntimeReadyGeneration] = useState(0);
    const runtimeReadyRef = useRef(false);
    const [readyTimedOut, setReadyTimedOut] = useState(false);
    const [runtimeError, setRuntimeError] = useState<{ phase: string; message: string } | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [bridgeListening, setBridgeListening] = useState(false);
    const [retryingRuntime, setRetryingRuntime] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const bridgeMessagesRef = useRef<Record<string, number>>({});
    const iframeLoadCountRef = useRef(0);
    const { t, i18n: activeI18n } = useTranslation();
    const notification = useNotification();
    const { activeTheme, isLoading: isThemeLoading, hasCachedTheme } = usePlatformTheme();
    const themeReady = !isThemeLoading || hasCachedTheme;
    const { data: sessionUser } = useSession();
    const runtimeFeatures = useMemo(() => resolveGateRuntimeFeatures(page), [page]);
    const currencyPreferences = useCurrencyPreferences({ enabled: runtimeFeatures.currency });

    const [guestLocale, setGuestLocale] = useState<string | undefined>(() => readStoredGuestLocale());
    const lastRequestedGuestLocale = useRef<string | null>(null);
    const appLocale = normalizeGateLocale(activeI18n.resolvedLanguage || activeI18n.language || i18n.resolvedLanguage || i18n.language);
    const browserLocale = normalizeGateLocale(window.navigator?.language);
    const userLocale = normalizeGateLocale(sessionUser?.preferredLanguage);
    const routeLocale = normalizeGateLocale(hostLocation.query?.lang);
    const isAuthenticated = Boolean(sessionUser?.id);
    const locale = isAuthenticated
        ? userLocale || appLocale || browserLocale || "en-US"
        : routeLocale || guestLocale || appLocale || browserLocale || "en-US";
    // Read by the bridge handler at invoke time without re-subscribing the message listener.
    const localeRef = useRef(locale);
    localeRef.current = locale;

    useEffect(() => {
        if (!isAuthenticated && routeLocale && routeLocale !== guestLocale) {
            writeStoredGuestLocale(routeLocale);
            setGuestLocale(routeLocale);
        }
    }, [guestLocale, isAuthenticated, routeLocale]);

    useEffect(() => {
        if (isAuthenticated) return;
        if (normalizeGateLocale(activeI18n.resolvedLanguage) === locale || normalizeGateLocale(activeI18n.language) === locale) {
            lastRequestedGuestLocale.current = null;
            return;
        }
        if (lastRequestedGuestLocale.current === locale) return;
        lastRequestedGuestLocale.current = locale;
        void activeI18n.changeLanguage(locale);
    }, [activeI18n, isAuthenticated, locale]);
    const fallbackLocale = Array.isArray(activeI18n.options?.fallbackLng)
        ? activeI18n.options.fallbackLng[0]
        : typeof activeI18n.options?.fallbackLng === "string"
            ? activeI18n.options.fallbackLng
            : "en-US";
    const normalizedFallbackLocale = normalizeGateLocale(fallbackLocale) || "en-US";
    const configuredPublicLocales = useMemo(() => readGateSupportedLocales(page), [page]);

    const supportedLocales = useMemo(() => (
        Array.from(new Set([
            normalizedFallbackLocale,
            locale,
            ...DEFAULT_GATE_TRANSLATION_LANGUAGES,
            ...configuredPublicLocales,
        ].filter(Boolean))) as string[]
    ), [configuredPublicLocales, locale, normalizedFallbackLocale]);

    const publicTranslationLanguages = useMemo(() => [...supportedLocales].sort(), [supportedLocales]);
    const { data: resolvedFormValidationTranslations = {} } = useTranslationResolver(
        FORM_VALIDATION_TRANSLATION_KEYS,
        locale,
        runtimeFeatures.translations && isAuthenticated
    );
    const resolvedFormValidationMessages = useMemo(() => (
        Object.entries(resolvedFormValidationTranslations).reduce<Record<string, unknown>>((messages, [key, value]) => {
            assignMessagePath(messages, key, value);
            return messages;
        }, {})
    ), [resolvedFormValidationTranslations]);

    const { data: publicTranslations = [] } = useQuery({
        queryKey: ["gate_public_translations", page.isPublic, publicTranslationLanguages],
        queryFn: async () => {
            const metric = page.slug || page.id || "gate-page";
            const startMark = `primum.gate.translations.${metric}.start`;
            performanceMark(startMark);

            try {
                return await fetchGatePublicTranslations(publicTranslationLanguages, page.isPublic);
            } finally {
                performanceMeasure(`primum.gate.translations.${metric}`, startMark);
            }
        },
        enabled: runtimeFeatures.translations && Boolean(publicTranslationLanguages.length),
        placeholderData: keepPreviousData,
        retry: false,
    });

    const messages = useMemo(() => {
        const globalMessages = activeI18n.getResourceBundle(locale, "translation")
            || i18n.getResourceBundle(locale, "translation")
            || {};
        const inlineMessages = readGateInlineMessages(page, locale);
        const fallbackPublicMessages = translationsToMessages(publicTranslations, normalizedFallbackLocale);
        const localePublicMessages = translationsToMessages(publicTranslations, locale);

        return [fallbackPublicMessages, globalMessages, inlineMessages, localePublicMessages, resolvedFormValidationMessages]
            .reduce<Record<string, unknown>>((merged, nextMessages) => mergeMessages(merged, nextMessages), {});
    }, [activeI18n, locale, normalizedFallbackLocale, page, publicTranslations, resolvedFormValidationMessages]);

    const messagesByLocale = useMemo(() => {
        return publicTranslationLanguages.reduce<Record<string, Record<string, unknown>>>((accumulator, language) => {
            const globalMessages = activeI18n.getResourceBundle(language, "translation")
                || i18n.getResourceBundle(language, "translation")
                || {};
            const inlineMessages = readGateInlineMessages(page, language);
            const translationMessages = translationsToMessages(publicTranslations, language);
            const resolvedMessages = language === locale ? resolvedFormValidationMessages : {};

            accumulator[language] = [globalMessages, inlineMessages, translationMessages, resolvedMessages]
                .reduce<Record<string, unknown>>((merged, nextMessages) => mergeMessages(merged, nextMessages), {});

            return accumulator;
        }, {});
    }, [activeI18n, locale, page, publicTranslationLanguages, publicTranslations, resolvedFormValidationMessages]);

    const runtimeCurrency = useMemo<GateRuntimeCurrencyPayload>(() => ({
        currencies: currencyPreferences.data.currencies,
        defaultCurrencyCode: currencyPreferences.data.defaultCurrencyCode ?? null,
        defaultNumberFormat: currencyPreferences.data.defaultNumberFormat ?? null,
        userNumberFormat: sessionUser?.numberFormat ?? null,
    }), [currencyPreferences.data, sessionUser?.numberFormat]);

    const runtimeSession = useMemo(() => sessionUser ? {
        id: sessionUser.id,
        name: sessionUser.name,
        email: sessionUser.email,
        preferredLanguage: sessionUser.preferredLanguage,
        preferred_language: sessionUser.preferredLanguage,
        numberFormat: sessionUser.numberFormat,
        number_format: sessionUser.numberFormat,
        dateTimeFormat: sessionUser.dateTimeFormat,
        date_time_format: sessionUser.dateTimeFormat,
        timeZone: sessionUser.timeZone,
        time_zone: sessionUser.timeZone,
        roles: sessionUser.roles,
        groups: sessionUser.groups,
    } : null, [sessionUser]);

    const initialRuntimeI18n = useMemo(() => ({
        locale,
        fallbackLocale: normalizedFallbackLocale,
        messages,
        supportedLocales,
        isAuthenticated,
    }), [isAuthenticated, locale, messages, normalizedFallbackLocale, supportedLocales]);

    const runtimeI18n = useMemo(() => ({
        ...initialRuntimeI18n,
        messagesByLocale,
    }), [initialRuntimeI18n, messagesByLocale]);

    // Fetched once by the host (real origin, cacheable normally) and embedded inline into the
    // srcDoc below. The Gate iframe is sandboxed without `allow-same-origin`, which gives it an
    // opaque origin that can never read the browser's HTTP cache for a same-path `<script src>` —
    // so a separate fetch from inside the iframe would never benefit from caching no matter how
    // many times the file was already downloaded. Embedding it inline trades that always-uncached
    // network round trip for a parse/execute cost only, paid once per iframe boot either way.
    const { data: vueRuntimeSource, status: runtimeSourceStatus, isError: runtimeSourceError, refetch: refetchRuntimeSource } = useQuery({
        queryKey: GATE_VUE_RUNTIME_QUERY_KEY,
        queryFn: fetchGateVueRuntimeSource,
        staleTime: Infinity,
        retry: false,
    });

    const runtimeDocumentKey = [
        page.id || page.slug,
        page.rawRecord?.updated_at || "",
        JSON.stringify(runtimeFeatures),
    ].join(":");
    const runtimeDocumentRef = useRef<{ key: string; html: string }>();

    if (themeReady && vueRuntimeSource && (!runtimeDocumentRef.current || runtimeDocumentRef.current.key !== runtimeDocumentKey)) {
        const buildHtmlMetric = page.slug || page.id || "gate-page";
        const buildHtmlStartMark = `primum.gate.build-html.${buildHtmlMetric}.start`;
        performanceMark(buildHtmlStartMark);

        const html = buildGateRuntimeHtml({
            page,
            allowedMethods: page.apiScope || [],
            i18n: initialRuntimeI18n,
            location: initialHostLocationRef.current,
            vueRuntimeSource,
            theme: activeTheme,
            currency: runtimeCurrency,
            session: runtimeSession,
            features: runtimeFeatures,
        });

        performanceMeasure(`primum.gate.build-html.${buildHtmlMetric}`, buildHtmlStartMark);

        runtimeDocumentRef.current = {
            key: runtimeDocumentKey,
            html,
        };
    }

    const runtimeHtml = runtimeDocumentRef.current?.html || "";
    const runtimeSourceFailed = runtimeSourceError && !retryingRuntime;
    const recordGateDiagnostic = useCallback((reason: GateDiagnosticReason, extra: { error?: string | null; phase?: string | null; changed?: string[]; renders?: number } = {}) => {
        const entry: GateDiagnostic = {
            ts: new Date().toISOString(),
            slug: page.slug || page.id || "gate-page",
            reason,
            iframeLoadCount: iframeLoadCountRef.current,
            srcDocLength: runtimeHtml.length,
            bridgeMessages: { ...bridgeMessagesRef.current },
            runtimeSourceStatus,
            error: extra.error || null,
            phase: extra.phase || null,
            ...(extra.changed ? { changed: extra.changed, renders: extra.renders } : {}),
        };
        const entries = Array.isArray(window.__PRM_GATE_DIAG) ? window.__PRM_GATE_DIAG : [];
        window.__PRM_GATE_DIAG = [...entries, entry].slice(-20);
    }, [page.slug, page.id, runtimeHtml.length, runtimeSourceStatus]);
    const themeCssVariables = useMemo(() => Object.entries(platformThemeCssVariables(activeTheme))
        .reduce<Record<string, string>>((variables, [name, value]) => {
            variables[name] = String(value);
            return variables;
        }, {}), [activeTheme]);
    const { renderLoopDetected, resetRenderLoopGuard } = useRenderLoopGuard({
        hostLocation, page, supportedLocales, locale, messages, messagesByLocale,
        runtimeSession, runtimeCurrency, activeTheme, themeCssVariables, notification,
        navigate, runtimeFeatures, runtimeHtml, publicTranslations,
    }, (details) => recordGateDiagnostic("render-loop", { changed: details.changed, renders: details.renders }));
    const terminalFailure = retryCount >= 3 && (readyTimedOut || runtimeSourceFailed || renderLoopDetected || Boolean(runtimeError));
    useEffect(() => {
        if (runtimeSourceStatus === "success") setRetryingRuntime(false);
    }, [runtimeSourceStatus]);
    useEffect(() => {
        if (!themeReady || runtimeReady || readyTimedOut || runtimeSourceFailed || renderLoopDetected || runtimeError || terminalFailure) return;
        const timer = window.setTimeout(() => {
            if (runtimeReadyRef.current) return;
            const iframe = iframeRef.current;
            let iframeDocumentReadyState: DocumentReadyState | null = null;
            let iframeWindowAccessible = false;
            try {
                iframeWindowAccessible = Boolean(iframe?.contentWindow);
                iframeDocumentReadyState = iframe?.contentDocument?.readyState || null;
            } catch (_error) {
                // A sandboxed Gate iframe can deny document access.
            }
            console.error("GATE_READY_TIMEOUT", {
                slug: page.slug || page.id,
                elapsedMs: GATE_READY_TIMEOUT_MS,
                gateMarks: performance.getEntriesByType?.("mark")
                    .filter((entry) => entry.name.startsWith("primum.gate."))
                    .map((entry) => entry.name) || [],
                readyState: document.readyState,
                iframeDocumentReadyState,
                iframeWindowAccessible,
                lastRecordManagementError: getLastRecordManagementError?.() || null,
                runtimeSourceStatus,
                runtimeHtmlLength: runtimeHtml.length,
                bridgeMessages: { ...bridgeMessagesRef.current },
            });
            recordGateDiagnostic("timeout");
            setReadyTimedOut(true);
        }, GATE_READY_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [themeReady, runtimeHtml, runtimeReady, readyTimedOut, runtimeSourceFailed, runtimeSourceStatus, reloadKey, page.slug, page.id, renderLoopDetected, runtimeError, retryCount, recordGateDiagnostic]);

    const tryAgain = () => {
        if (retryCount >= 3) return;
        setRetryCount((current) => current + 1);
        resetRenderLoopGuard();
        runtimeReadyRef.current = false;
        setRuntimeReady(false);
        setReadyTimedOut(false);
        setRuntimeError(null);
        bridgeMessagesRef.current = {};
        setReloadKey((current) => current + 1);
        if (runtimeSourceError) {
            setRetryingRuntime(true);
            void Promise.resolve(refetchRuntimeSource()).finally(() => setRetryingRuntime(false));
        }
    };
    useEffect(() => {
        if (renderLoopDetected || terminalFailure) return;
        const widgetScopeMap = new Map((page.widgets || []).map((widget) => [widget.code, widget.apiScope || []]));

        const handleMessage = async (event: MessageEvent<GateBridgeRequestMessage | {
            source?: string;
            type?: string;
            payload?: {
                locale?: unknown;
                timing?: {
                    vueScriptMs?: number | null;
                    mountMs?: number;
                    vueResourceTiming?: { transferSize?: number; queueMs?: number; ttfbMs?: number; downloadMs?: number; totalFetchMs?: number } | null;
                };
                phase?: string;
                message?: string;
                stack?: string;
                url?: string;
            };
            locale?: unknown;
        }>) => {
            if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
                return;
            }

            if (!event.data || event.data.source !== "gate-runtime") {
                return;
            }

            const messageType = event.data.type || "unknown";
            bridgeMessagesRef.current[messageType] = (bridgeMessagesRef.current[messageType] || 0) + 1;

            if (event.data.type === "gate:error") {
                const payload = event.data.payload;
                const phase = typeof payload?.phase === "string" ? payload.phase : "window-error";
                const message = typeof payload?.message === "string" ? payload.message : "Gate runtime error";
                recordGateDiagnostic("runtime-error", { phase, error: message });
                console.error("GATE_RUNTIME_ERROR", { slug: page.slug || page.id, phase, message, stack: payload?.stack, url: payload?.url });
                setRuntimeError({ phase, message });
                return;
            }

            if (event.data.type === "gate:ready") {
                if (runtimeError) return;
                runtimeReadyRef.current = true;
                setRuntimeReady(true);
                setRuntimeReadyGeneration((current) => current + 1);
                setReadyTimedOut(false);
                setRetryCount(0);
                performanceMark("primum.gate.ready");
                performanceMeasure("primum.gate.iframe-to-ready", "primum.gate.iframe.create");
                const runtimeTiming = "payload" in event.data ? event.data.payload?.timing : undefined;
                logGateLoadTimings(page.slug || page.id || "gate-page", runtimeTiming);
                return;
            }

            if (event.data.type === "gate:locale-change") {
                const nextLocale = normalizeGateLocale("payload" in event.data ? event.data.payload?.locale : undefined)
                    || normalizeGateLocale("locale" in event.data ? event.data.locale : undefined);

                if (!isAuthenticated && nextLocale && supportedLocales.includes(nextLocale)) {
                    writeStoredGuestLocale(nextLocale);
                    setGuestLocale(nextLocale);
                }
                return;
            }

            if (event.data.type !== "gate:invoke") {
                return;
            }

            const request = event.data as GateBridgeRequestMessage;
            const responseBase: Pick<GateBridgeResponseMessage, "source"> = {
                source: "gate-host",
            };

            if (!isGateMethodAllowed(page.apiScope || [], request.method)) {
                postGateMessage(iframeRef.current.contentWindow, {
                    ...responseBase,
                    type: "gate:reject",
                    requestId: request.requestId,
                    error: `Gate API scope denied for method "${request.method}"`,
                });
                return;
            }

            if (request.widgetCode) {
                const widgetScope = widgetScopeMap.get(request.widgetCode);

                if (!widgetScope?.includes(request.method as GateApiScope)) {
                    postGateMessage(iframeRef.current.contentWindow, {
                        ...responseBase,
                        type: "gate:reject",
                        requestId: request.requestId,
                        error: `Gate widget API scope denied for method "${request.method}" on widget "${request.widgetCode}"`,
                    });
                    return;
                }
            }

            try {
                const payload = await invokeGateHostMethodWithContext(request.method, request.payload, {
                    navigate,
                    basePath: resolveGateBasePath(hostLocation),
                    locale: localeRef.current,
                    notify: (message, level = "info") => {
                        const normalizedLevel = ["success", "error", "warning", "info"].includes(level)
                            ? level as "success" | "error" | "warning" | "info"
                            : "info";

                        notification[normalizedLevel === "error" ? "alert" : normalizedLevel](
                            "gate-notify",
                            message
                        );
                    },
                });

                postGateMessage(iframeRef.current?.contentWindow, {
                    ...responseBase,
                    type: "gate:resolve",
                    requestId: request.requestId,
                    payload,
                });
            } catch (error) {
                postGateMessage(iframeRef.current?.contentWindow, {
                    ...responseBase,
                    type: "gate:reject",
                    requestId: request.requestId,
                    error: extractGateInvokeError(error),
                });
            }
        };

        window.addEventListener("message", handleMessage);
        setBridgeListening(true);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [hostLocation, isAuthenticated, navigate, notification, page.apiScope, page.widgets, page.slug, page.id, supportedLocales, renderLoopDetected, runtimeError, terminalFailure, recordGateDiagnostic]);

    useEffect(() => {
        if (renderLoopDetected || terminalFailure) return;
        setRuntimeReady(false);
        if (typeof performance !== "undefined" && performance.mark) performance.mark("primum.gate.iframe.create");
    }, [runtimeDocumentKey, renderLoopDetected, terminalFailure]);

    useEffect(() => {
        if (renderLoopDetected || !runtimeReady || !iframeRef.current?.contentWindow) return;
        postGateMessage(iframeRef.current.contentWindow, {
            source: "gate-host",
            type: "gate:context-sync",
            payload: {
                i18n: runtimeI18n,
                theme: activeTheme,
                themeCssVariables,
                currency: runtimeCurrency,
                session: runtimeSession,
            },
        });
    }, [activeTheme, runtimeCurrency, runtimeI18n, runtimeReady, runtimeReadyGeneration, runtimeSession, themeCssVariables, renderLoopDetected]);

    useEffect(() => {
        if (renderLoopDetected || !runtimeReady || !iframeRef.current?.contentWindow) return;
        postGateMessage(iframeRef.current.contentWindow, {
            source: "gate-host",
            type: "gate:location-sync",
            payload: hostLocation,
        });
    }, [hostLocation, runtimeReady, runtimeReadyGeneration, renderLoopDetected]);

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                background: "var(--prm-surface, #ffffff)",
            }}
        >
            {!renderLoopDetected && !terminalFailure && bridgeListening && Boolean(runtimeHtml) && <iframe
                key={reloadKey}
                ref={iframeRef}
                onLoad={() => { iframeLoadCountRef.current += 1; }}
                sandbox="allow-scripts allow-forms allow-popups allow-modals"
                allow="clipboard-write"
                srcDoc={runtimeHtml}
                title={t(`gate.page.${page.slug}.title`, { defaultValue: page.title || page.name }) || page.title || page.name}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                    opacity: runtimeReady ? 1 : 0,
                    transition: "opacity 180ms ease",
                }}
            />}
            <div
                role="status"
                aria-label="Loading Gate page"
                aria-hidden={Boolean(runtimeReady || readyTimedOut || runtimeSourceFailed || renderLoopDetected || runtimeError || terminalFailure)}
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    padding: 24,
                    pointerEvents: "none",
                    opacity: runtimeReady || readyTimedOut || runtimeSourceFailed || renderLoopDetected || runtimeError || terminalFailure ? 0 : 1,
                    transition: "opacity 180ms ease",
                    background: "var(--prm-surface, #ffffff)",
                }}
            >
                <div style={{ width: "38%", maxWidth: 280, height: 24, borderRadius: 6, background: "rgba(148, 163, 184, 0.28)" }} />
                <div style={{ width: "72%", maxWidth: 520, height: 14, borderRadius: 6, background: "rgba(148, 163, 184, 0.20)" }} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, width: "100%" }}>
                    {[0, 1, 2].map((item) => (
                        <div
                            key={item}
                            style={{
                                height: 88,
                                borderRadius: 8,
                                border: "1px solid rgba(148, 163, 184, 0.22)",
                                background: "linear-gradient(90deg, rgba(148, 163, 184, 0.10), rgba(148, 163, 184, 0.20), rgba(148, 163, 184, 0.10))",
                            }}
                        />
                    ))}
                </div>
                <div style={{ flex: 1, minHeight: 96, borderRadius: 8, background: "rgba(148, 163, 184, 0.12)" }} />
            </div>
            {(readyTimedOut || runtimeSourceFailed || renderLoopDetected || runtimeError || terminalFailure) && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--prm-bg, #ffffff)" }}>
                <div role="alert" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, maxWidth: 480, padding: 32, borderRadius: 12, border: "1px solid var(--prm-border, #d1d5db)", background: "var(--prm-surface, #ffffff)", color: "var(--prm-text-primary, #111827)", textAlign: "center" }}>
                    <Typography variant="h6">{t("This page could not be loaded")}</Typography>
                    <Typography variant="body2">{t("Something went wrong while loading this page. You can try again or go back to the home page.")}</Typography>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12 }}>
                        {retryCount < 3 && <Button type="button" variant="contained" color="primary" onClick={tryAgain}>{t("Try again")}</Button>}
                        <Button type="button" variant="outlined" color="primary" onClick={() => navigate(resolveGateBasePath(hostLocation))}>{t("Back to home")}</Button>
                    </div>
                </div>
            </div>}
        </div>
    );
};
export default PortalFrameComponent;
